import useSWR, { Fetcher } from 'swr';
import { ScheduleWithPermissions as Schedule } from '@/types/schedule';
import { addSchedule, deleteSchedule, updateSchedule, upgradeSchedule } from '@/lib/api/schedule';
import { ScheduleCreateInput, ScheduleUpdateInput, ScheduleUpgradeInput } from '@/models/schema';
import { produce } from 'immer';
import { replaceObjectInArray } from '@/lib/utils';
import { useCalendarStore } from '@/hooks/use-calendar-store';

const fetcher: Fetcher<Schedule[], string> = (url: string) => fetch(url).then((res) => res.json());

export function useSchedules() {
  const { data, error, isLoading, mutate } = useSWR(`/api/schedules`, fetcher);
  const { setSchedule } = useCalendarStore();

  const add = async (input: ScheduleCreateInput) => {
    await mutate<Schedule[]>(
      async () => {
        const action = await addSchedule(input);

        return [...(data ?? []), action];
      },
      {
        revalidate: true,
        rollbackOnError: true,
      },
    );
  };

  const update = async (input: ScheduleUpdateInput) => {
    const action = updateSchedule(input);
    let newSchedule: Schedule | null = null;

    await mutate<Schedule[]>(
      async () => {
        newSchedule = await action;

        return replaceObjectInArray(newSchedule, data ?? []);
      },
      {
        optimisticData: () => {
          return (
            produce(data, (draft) => {
              const index = draft!.findIndex((t) => t.id === input.scheduleId);
              if (index !== -1) {
                draft![index] = {
                  ...draft![index],
                  ...input,
                };
                setSchedule(draft![index]);
              }
            }) ?? []
          );
        },
        revalidate: false,
      },
    );

    return newSchedule!;
  };

  const remove = async (id: string) => {
    return await mutate<Schedule[]>(
      async () => {
        await deleteSchedule(id);

        const newSchedules = data?.filter((t) => t.id !== id);
        return [...(newSchedules ?? [])];
      },
      {
        optimisticData: () => data?.filter((t) => t.id !== id) ?? [],
        revalidate: false,
        throwOnError: true,
      },
    );
  };

  const upgrade = async (input: ScheduleUpgradeInput) => {
    const action = upgradeSchedule(input);
    let newSchedule: Schedule | null = null;

    await mutate<Schedule[]>(
      async () => {
        newSchedule = await action;

        return replaceObjectInArray(newSchedule, data ?? []);
      },
      {
        optimisticData: () => {
          return (
            produce(data, (draft) => {
              const index = draft!.findIndex((t) => t.id === input.scheduleId);
              if (index !== -1) {
                draft![index] = {
                  ...draft![index],
                  ...input,
                };
              }
            }) ?? []
          );
        },
        revalidate: false,
      },
    );

    return newSchedule!;
  };

  return {
    schedules: data ?? [],
    isLoading,
    isError: error,
    mutate,
    add,
    update,
    upgrade,
    remove,
  };
}
